import React from 'react';
import ActionMenu from '@components/RingilComponents/ActionMenu.tsx';
import SvgShuffle from '@icons/shuffle.svg?react';
import SvgArrowRightDown from '@icons/arrowDown.svg?react';
import SvgDownload from '@icons/download.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ExpeditionLocality } from '@app/pages/expedition/create/types.ts';

interface ComponentProps {
  locations: ExpeditionLocality[];
  /** Stops the order may be unloaded at; anything else is left out of the menu. */
  candidateIndexes: number[];
  onMove: (targetLocIndex: number) => void;
  /** Shows the trigger as a labelled button rather than a bare icon. */
  label?: string;
  dataTestId?: string;
}

/**
 * Moves one order's unloading to a different stop.
 *
 * The end of the route is named rather than listed by stop, and carries its own glyph — it is where
 * anything not dropped off earlier ends up, so it reads as a destination rather than as one stop
 * among the others.
 *
 * `ActionMenu` renders nothing when there is nowhere valid to move to, so the button disappears
 * instead of opening an empty menu.
 */
const MoveOrderDropdownButton: React.FC<ComponentProps> = ({ locations, candidateIndexes, onMove, label, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const lastIndex = locations.length - 1;

  return (
    <ActionMenu
      icon={<SvgShuffle className={'size-4 shrink-0'} />}
      label={label}
      mode={label ? 'outlined-rectangle' : 'table'}
      tooltip={label ? undefined : tr('CreateExpedition.moveOrderToLocation', 'Move order to location')}
      heading={tr('CreateExpedition.unloadOrderAtAnotherLocation', 'Unload order at another location')}
      dataTestId={dataTestId}
      items={candidateIndexes.map(idx => ({
        key: String(idx),
        label:
          idx === lastIndex
            ? tr('CreateExpedition.unloadAtEndOfRoute', 'At the end of the route')
            : locations[idx].location?.locationName ||
              locations[idx].manualAddress?.name ||
              tr('CreateExpedition.moveOrderToLocationLabelForUnNamed', 'Stop {{stop}}', { stop: idx + 1 }),
        icon: idx === lastIndex ? <SvgDownload /> : <SvgArrowRightDown />,
        onClick: () => onMove(idx),
        dataTestId: dataTestId ? `${dataTestId}-stop${idx + 1}` : undefined,
      }))}
    />
  );
};

export default MoveOrderDropdownButton;